import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { fetchWithAuth } from "../../../../utils/api";
import { AuthContext } from "../../../../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faDownload,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../utils/swalAlert";
import Swal from "sweetalert2";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageEnrollments = forwardRef(({ classId }, ref) => {
  const { user } = useContext(AuthContext);
  const [instituteStudents, setInstituteStudents] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInstituteEnrollModalOpen, setIsInstituteEnrollModalOpen] =
    useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [isEnrollingStudents, setIsEnrollingStudents] = useState(false);
  const [isLoadingModalData, setIsLoadingModalData] = useState(false);
  const [isParsingExcel, setIsParsingExcel] = useState(false);
  const [bulkProgress, setBulkProgress] = useState("");
  const [bulkPhase, setBulkPhase] = useState("");
  const [currentStudentProcessing, setCurrentStudentProcessing] = useState("");

  // Excel file input ref
  const excelFileInputRef = useRef(null);

  // Multi-select enrollment deletion states
  const [selectedEnrollmentIds, setSelectedEnrollmentIds] = useState(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // QR Download states
  const [downloadingAllQR, setDownloadingAllQR] = useState(false);

  // Institute category and filter options
  const [instituteCategory, setInstituteCategory] = useState(null);
  const isLanguageInst =
    instituteCategory === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE";
  const [levelOptions, setLevelOptions] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);

  // Batch filter state (dynamically extracted from students)
  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const hasFetched = useRef(false);
  const tableRef = useRef(null);

  // Track filtered data from Table component
  const [filteredStudents, setFilteredStudents] = useState([]);

  // Check if students have level data for conditional column rendering
  const hasLevelData = useMemo(() => {
    return students.some(
      (stu) =>
        stu.level !== null && stu.level !== undefined && stu.level !== "",
    );
  }, [students]);

  // Check if students have batch data for conditional column rendering
  const hasBatchData = useMemo(() => {
    return students.some(
      (stu) =>
        stu.batch !== null && stu.batch !== undefined && stu.batch !== "",
    );
  }, [students]);

  // Memoized callback for filtered data to prevent infinite loops
  const handleDataFiltered = useCallback((data) => {
    setFilteredStudents(data);
  }, []);

  // Expose refresh method to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: () => fetchClassStudents(),
  }));

  // Fetch Students from API
  const fetchClassStudents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/enrollment/get-all/${classId}`,
        {
          credentials: "include",
        },
      );

      if (!response) return;

      if (!response.ok) throw new Error("Failed to fetch students");
      const data = await response.json();

      // Add fullName by joining firstName and lastName
      const transformedData = data.map((student) => ({
        ...student,
        name: `${student.firstName} ${student.lastName}`,
        rollNo: student.rollNo || "",
      }));

      setStudents(transformedData);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [classId]);

  // Fetch institute data to get category
  const fetchInstitute = useCallback(async () => {
    if (!user?.instituteId) return;
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get/${user.instituteId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) return;

      const data = await response.json();
      setInstituteCategory(data.category || "K12_SCHOOL");

      // Set level options if available from institute
      if (data.levels && data.levels.length > 0) {
        const levelOpts = data.levels.map((level) => ({
          value: level,
          label: level,
        }));
        setLevelOptions([
          { value: "all", label: "All Levels" },
          { value: "-", label: "No Level" },
          ...levelOpts,
        ]);
      } else if (data.category !== "HIGHER_EDUCATION") {
        // Fallback: Fetch levels from LOV
        try {
          const lovResponse = await fetchWithAuth(
            `${serverUrl}/api/lov/get-all-by-type/level`,
            {
              method: "GET",
              credentials: "include",
            },
          );
          if (lovResponse && lovResponse.ok) {
            const lovData = await lovResponse.json();
            const levelOpts = lovData
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((level) => ({
                value: level.value,
                label: level.value,
              }));
            setLevelOptions([
              { value: "all", label: "All Levels" },
              { value: "-", label: "No Level" },
              ...levelOpts,
            ]);
          }
        } catch (lovError) {
          console.error("Failed to fetch levels from LOV:", lovError);
        }
      }
    } catch (error) {
      console.error("Failed to fetch institute:", error);
    }
  }, [user?.instituteId]);

  const fetchInstituteStudents = useCallback(async () => {
    if (!user?.instituteId) return;
    try {
      const instituteResponse = await fetchWithAuth(
        `${serverUrl}/api/user/get-all/${user.instituteId}/student`,
        {
          credentials: "include",
        },
      );

      if (instituteResponse && instituteResponse.ok) {
        const instituteData = await instituteResponse.json();
        const filteredInstituteStudents = instituteData.filter(
          (stu) =>
            stu.role.includes("student") &&
            stu.instituteId === user.instituteId,
        );
        setInstituteStudents(filteredInstituteStudents);
      }
    } catch (error) {
      console.log(error);
    }
  }, [user?.instituteId]);

  // Build batch options from institute students (dynamically)
  const buildBatchOptions = useCallback((students) => {
    const uniqueBatches = [
      ...new Set(
        students
          .map((stu) => stu.batch)
          .filter(
            (batch) => batch !== null && batch !== undefined && batch !== "",
          ),
      ),
    ].sort((a, b) => b - a); // Sort descending (most recent year first)

    if (uniqueBatches.length > 0) {
      const batchOpts = uniqueBatches.map((batch) => ({
        value: batch,
        label: String(batch),
      }));
      const options = [
        { value: "all", label: "All Batches" },
        { value: "-", label: "No Batch" },
        ...batchOpts,
      ];
      setBatchOptions(options);
    } else {
      setBatchOptions([]);
    }
  }, []);

  // Build level options from institute students (dynamically)
  const buildLevelOptions = useCallback((students) => {
    const uniqueLevels = [
      ...new Set(
        students
          .map((stu) => stu.level)
          .filter(
            (level) => level !== null && level !== undefined && level !== "",
          ),
      ),
    ].sort(); // Sort alphabetically

    if (uniqueLevels.length > 0) {
      const levelOpts = uniqueLevels.map((level) => ({
        value: level,
        label: level,
      }));
      const options = [
        { value: "all", label: "All Levels" },
        { value: "-", label: "No Level" },
        ...levelOpts,
      ];
      setLevelOptions(options);
    } else {
      setLevelOptions([]);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    if (classId) {
      fetchClassStudents();
      fetchInstitute();
      hasFetched.current = true;
    } else {
      setError("Invalid class ID.");
      setLoading(false);
    }
  }, [classId, fetchClassStudents, fetchInstitute]);

  // Get students available for enrollment (not already enrolled + filtered by batch or level)
  const availableStudents = useMemo(() => {
    const enrolledStudentIds = new Set(students.map((s) => s._id));

    return instituteStudents
      .filter((stu) => !enrolledStudentIds.has(stu._id)) // Exclude already enrolled
      .filter((stu) => {
        // Level filter (for non-HIGHER_EDUCATION)
        if (
          instituteCategory !== "HIGHER_EDUCATION" &&
          selectedLevel &&
          selectedLevel.value !== "all"
        ) {
          if (selectedLevel.value === "-") {
            if (stu.level && stu.level !== "") {
              return false;
            }
          } else if (stu.level !== selectedLevel.value) {
            return false;
          }
        }
        // Batch filter (for HIGHER_EDUCATION)
        if (
          instituteCategory === "HIGHER_EDUCATION" &&
          selectedBatch &&
          selectedBatch.value !== "all"
        ) {
          if (selectedBatch.value === "-") {
            if (
              stu.batch !== null &&
              stu.batch !== undefined &&
              stu.batch !== ""
            ) {
              return false;
            }
          } else if (stu.batch !== selectedBatch.value) {
            return false;
          }
        }
        return true;
      })
      .map((stu) => ({
        ...stu,
        name: `${stu.firstName} ${stu.lastName}`,
      }));
  }, [
    instituteStudents,
    students,
    selectedLevel,
    selectedBatch,
    instituteCategory,
  ]);

  // Open Institute Enroll Modal
  const openInstituteEnrollModal = async () => {
    setSelectedStudentIds(new Set());
    if (excelFileInputRef.current) {
      excelFileInputRef.current.value = "";
    }
    if (instituteCategory !== "HIGHER_EDUCATION") {
      setSelectedLevel({ value: "all", label: "All Levels" });
    } else {
      setSelectedBatch({ value: "all", label: "All Batches" });
    }
    setIsInstituteEnrollModalOpen(true);
    setIsLoadingModalData(true);

    try {
      // Fetch institute students when modal opens
      await fetchInstituteStudents();
      // Build level/batch options from the fetched students
      // Done in useEffect when instituteStudents state updates
    } finally {
      setIsLoadingModalData(false);
    }
  };

  // Build level/batch options when instituteStudents changes
  useEffect(() => {
    if (instituteStudents.length > 0) {
      if (instituteCategory !== "HIGHER_EDUCATION") {
        buildLevelOptions(instituteStudents);
      } else {
        buildBatchOptions(instituteStudents);
      }
    }
  }, [
    instituteStudents,
    buildBatchOptions,
    buildLevelOptions,
    instituteCategory,
  ]);

  // Close Institute Enroll Modal
  const closeInstituteEnrollModal = () => {
    setIsInstituteEnrollModalOpen(false);
    setSelectedStudentIds(new Set());
    if (excelFileInputRef.current) {
      excelFileInputRef.current.value = "";
    }
  };

  // Handle Excel upload and student matching
  const handleExcelUpload = async (event) => {
    if (isParsingExcel) return;
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsParsingExcel(true);
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];

      if (!worksheet) {
        swalError("No worksheet found in the Excel file.");
        return;
      }

      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (!jsonData || jsonData.length === 0) {
        swalWarning("No valid data found in the Excel file.");
        return;
      }

      const headerRow = jsonData[0] || [];
      const dataRows = jsonData
        .slice(1)
        .filter(
          (row) =>
            Array.isArray(row) &&
            row.some(
              (cell) =>
                cell !== undefined &&
                cell !== null &&
                String(cell).trim() !== "",
            ),
        );

      if (dataRows.length === 0) {
        swalWarning("The Excel file does not contain any student records.");
        return;
      }

      const headerMap = {};

      headerRow.forEach((cell, colIndex) => {
        const headerVal = String(cell || "")
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
        if (
          headerVal.includes("email") &&
          !headerVal.includes("message") &&
          !headerVal.includes("msg")
        ) {
          headerMap.email = colIndex;
        } else if (
          headerVal.includes("referenceid") ||
          headerVal.includes("refid") ||
          headerVal.includes("reference") ||
          headerVal.includes("admissionid") ||
          headerVal.includes("enrollmentno") ||
          headerVal === "id"
        ) {
          headerMap.referenceId = colIndex;
        } else if (headerVal.includes("firstname") || headerVal === "first") {
          headerMap.firstName = colIndex;
        } else if (headerVal.includes("lastname") || headerVal === "last") {
          headerMap.lastName = colIndex;
        }
      });

      if (headerMap.email === undefined && headerMap.referenceId === undefined) {
        swalError(
          "Could not identify 'email' or 'referenceId' columns in the Excel file. Please ensure column headers match the export format.",
        );
        return;
      }

      // Existing enrolled student IDs in current class
      const enrolledStudentIds = new Set(students.map((s) => s._id));

      const matchedUnenrolledStudents = [];
      const alreadyEnrolledStudents = [];
      const notFoundRows = [];
      const newSelectedIds = new Set(selectedStudentIds);

      dataRows.forEach((row, index) => {
        const rowEmail =
          headerMap.email !== undefined
            ? String(row[headerMap.email] ?? "").trim().toLowerCase()
            : "";
        const rowRefId =
          headerMap.referenceId !== undefined
            ? String(row[headerMap.referenceId] ?? "").trim().toLowerCase()
            : "";
        const rowName =
          `${row[headerMap.firstName] || ""} ${
            row[headerMap.lastName] || ""
          }`.trim() ||
          rowEmail ||
          rowRefId ||
          `Row ${index + 2}`;

        if (!rowEmail && !rowRefId) {
          return;
        }

        // Match against instituteStudents
        const foundStudent = instituteStudents.find((stu) => {
          const stuEmail = stu.email
            ? String(stu.email).trim().toLowerCase()
            : "";
          const stuRefId =
            stu.referenceId !== undefined && stu.referenceId !== null
              ? String(stu.referenceId).trim().toLowerCase()
              : "";

          if (rowEmail && stuEmail && rowEmail === stuEmail) return true;
          if (rowRefId && stuRefId && rowRefId === stuRefId) return true;
          return false;
        });

        if (foundStudent) {
          if (enrolledStudentIds.has(foundStudent._id)) {
            alreadyEnrolledStudents.push({
              name: `${foundStudent.firstName} ${foundStudent.lastName}`,
              email: foundStudent.email || "-",
              referenceId: foundStudent.referenceId || "-",
            });
          } else {
            if (!newSelectedIds.has(foundStudent._id)) {
              newSelectedIds.add(foundStudent._id);
              matchedUnenrolledStudents.push(foundStudent);
            }
          }
        } else {
          notFoundRows.push({
            name: rowName,
            email: rowEmail || "-",
            referenceId: rowRefId || "-",
          });
        }
      });

      // Reset level/batch filter so matched students will be visible in the table
      if (instituteCategory !== "HIGHER_EDUCATION") {
        setSelectedLevel({ value: "all", label: "All Levels" });
      } else {
        setSelectedBatch({ value: "all", label: "All Batches" });
      }

      // Update selected student IDs
      setSelectedStudentIds(new Set(newSelectedIds));

      const matchedCount = matchedUnenrolledStudents.length;
      const alreadyEnrolledCount = alreadyEnrolledStudents.length;
      const notFoundCount = notFoundRows.length;

      // Create detailed table rows for each category
      const matchedRowsHtml = matchedUnenrolledStudents
        .map(
          (s) =>
            `<tr style="background: #d4edda;">
          <td style="padding: 8px; border: 1px solid #c3e6cb; text-align: center; color: #155724; font-weight: bold;">✓</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb; text-align: left;">${s.firstName} ${s.lastName}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb; text-align: left;">${s.referenceId || "-"}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb; text-align: left;">${s.email || "-"}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb; text-align: center; color: #155724; font-weight: 600;">Matched & Selected</td>
        </tr>`,
        )
        .join("");

      const alreadyEnrolledRowsHtml = alreadyEnrolledStudents
        .map(
          (s) =>
            `<tr style="background: #e8f4fd;">
          <td style="padding: 8px; border: 1px solid #bee5eb; text-align: center; color: #0c5460; font-weight: bold;">ℹ</td>
          <td style="padding: 8px; border: 1px solid #bee5eb; text-align: left;">${s.name}</td>
          <td style="padding: 8px; border: 1px solid #bee5eb; text-align: left;">${s.referenceId || "-"}</td>
          <td style="padding: 8px; border: 1px solid #bee5eb; text-align: left;">${s.email || "-"}</td>
          <td style="padding: 8px; border: 1px solid #bee5eb; text-align: center; color: #0c5460; font-weight: 600;">Already Enrolled</td>
        </tr>`,
        )
        .join("");

      const notFoundRowsHtml = notFoundRows
        .map(
          (s) =>
            `<tr style="background: #f8d7da;">
          <td style="padding: 8px; border: 1px solid #f5c6cb; text-align: center; color: #721c24; font-weight: bold;">✗</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb; text-align: left;">${s.name}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb; text-align: left;">${s.referenceId || "-"}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb; text-align: left;">${s.email || "-"}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb; text-align: center; color: #721c24; font-weight: 600;">Not Found in Institute</td>
        </tr>`,
        )
        .join("");

      const tableHtml = `
        <div style="max-height: 400px; overflow: auto; margin-top: 10px; -webkit-overflow-scrolling: touch;">
          <div style="min-width: 600px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; white-space: nowrap;">
              <thead>
                <tr style="background: #f8f9fa; position: sticky; top: 0; z-index: 1;">
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center; width: 40px;"></th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Student Name</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Reference ID</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Email</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${matchedRowsHtml}
                ${alreadyEnrolledRowsHtml}
                ${notFoundRowsHtml}
              </tbody>
            </table>
          </div>
        </div>
        <p style="margin-top: 8px; font-size: 0.75em; color: #888; text-align: center;">Swipe horizontally to see all columns</p>
        <div style="margin-top: 12px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
          <span style="color: #155724; font-weight: 600;">✓ Matched & Selected: ${matchedCount}</span>
          ${alreadyEnrolledCount > 0 ? `<span style="color: #0c5460; font-weight: 600;">ℹ Already Enrolled: ${alreadyEnrolledCount}</span>` : ""}
          ${notFoundCount > 0 ? `<span style="color: #721c24; font-weight: 600;">✗ Not Found: ${notFoundCount}</span>` : ""}
        </div>
      `;

      const icon =
        matchedCount > 0
          ? "success"
          : alreadyEnrolledCount > 0
            ? "info"
            : "error";

      const title =
        matchedCount > 0
          ? notFoundCount === 0 && alreadyEnrolledCount === 0
            ? "All Selected Students Matched"
            : "Excel Matching Summary"
          : alreadyEnrolledCount > 0
            ? "Students Already Enrolled"
            : "No Matches Found";

      Swal.fire({
        icon: icon,
        title: title,
        html: tableHtml,
        width: "90%",
        confirmButtonText: "Got it!",
      });
    } catch (err) {
      console.error("Excel upload error:", err);
      swalError("Failed to parse Excel file: " + (err.message || "Unknown error"));
    } finally {
      setIsParsingExcel(false);
      if (excelFileInputRef.current) {
        excelFileInputRef.current.value = "";
      }
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  // Handle row selection from Table component
  const handleRowSelect = (e, studentId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedStudentIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  // Handle select all from Table component
  const handleSelectAll = (visibleItemIds, areAllVisibleSelected) => {
    setSelectedStudentIds((prev) => {
      const newSet = new Set(prev);
      if (areAllVisibleSelected) {
        // Deselect all visible
        visibleItemIds.forEach((id) => newSet.delete(id));
      } else {
        // Select all visible
        visibleItemIds.forEach((id) => newSet.add(id));
      }
      return newSet;
    });
  };

  // Helper to extract filename from response header
  const getFilenameFromHeader = (response) => {
    const disposition = response.headers.get("content-disposition");
    if (!disposition) return null;

    const match = disposition.match(/filename="?([^"]+)"?/);
    return match ? match[1] : null;
  };

  // Download All QR Codes as PDF
  const handleDownloadAllQR = async () => {
    try {
      setDownloadingAllQR(true);

      const response = await fetchWithAuth(
        `${serverUrl}/api/enrollment/generate/students-qr-pdf/class/${classId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to download QR codes");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;

      // Extract filename from backend header
      const filename = getFilenameFromHeader(response) || `QR_${classId}.pdf`; // fallback

      a.download = filename;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      swalError(err.message || "Failed to download QR codes");
    } finally {
      setDownloadingAllQR(false);
    }
  };

  // Export enrollments to Excel
  const exportToExcel = async () => {
    // Use filtered data if available, otherwise use all students
    const dataToExport =
      filteredStudents.length > 0 ? filteredStudents : students;

    if (dataToExport.length === 0) {
      swalError("No students to export.");
      return;
    }

    const headers = [
      "firstName",
      "lastName",
      "referenceId",
      "rollNo",
      "email",
      "phoneNumber",
      "emailMessages",
      "whatsAppMessages",
    ];

    try {
      // Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Enrollments");

      // Add headers row with bold styling
      const headerRow = worksheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      });

      // Add data rows
      dataToExport.forEach((stu) => {
        const row = worksheet.addRow([
          stu.firstName || "",
          stu.lastName || "",
          stu.referenceId || "",
          stu.rollNo || "",
          stu.email || "",
          stu.phoneNumber || "",
          stu.receiveEmailMessages ? "Yes" : "No",
          stu.receiveWhatsAppMessages ? "Yes" : "No",
        ]);
        // Format phone number cell as text to prevent scientific notation
        const phoneCell = row.getCell(6); // Column F (phoneNumber)
        if (stu.phoneNumber) {
          phoneCell.value = String(stu.phoneNumber);
          phoneCell.numFmt = "@"; // Text format
        }
      });

      // Set column widths
      worksheet.columns = [
        { width: 15 }, // firstName
        { width: 15 }, // lastName
        { width: 15 }, // referenceId
        { width: 12 }, // rollNo
        { width: 30 }, // email
        { width: 20 }, // phoneNumber
        { width: 15 }, // emailMessages
        { width: 18 }, // whatsAppMessages
      ];

      // Generate filename with level/batch from data and date
      let categoryLabel = "All";
      if (instituteCategory !== "HIGHER_EDUCATION") {
        const uniqueLevels = [
          ...new Set(dataToExport.map((s) => s.level).filter(Boolean)),
        ];
        categoryLabel =
          uniqueLevels.length === 1
            ? uniqueLevels[0]
            : uniqueLevels.length > 1
              ? "Multiple_Levels"
              : "All_Levels";
      } else if (instituteCategory === "HIGHER_EDUCATION") {
        const uniqueBatches = [
          ...new Set(dataToExport.map((s) => s.batch).filter(Boolean)),
        ];
        categoryLabel =
          uniqueBatches.length === 1
            ? String(uniqueBatches[0])
            : uniqueBatches.length > 1
              ? "Multiple_Batches"
              : "All_Batches";
      }

      // Format date as dd-mm-yyyy
      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;
      const filename = `Enrollments_(${categoryLabel})_${dateStr}.xlsx`;

      // Write and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);
      swalSuccess("Exported successfully!");
    } catch (err) {
      swalError("Failed to export file.");
    }
  };

  // Handle Bulk Enrollment
  const handleBulkEnrollment = async () => {
    if (selectedStudentIds.size === 0) {
      swalWarning("Please select at least one student to enroll.");
      return;
    }

    setIsEnrollingStudents(true);
    setBulkPhase("enrolling");

    try {
      let enrolledCount = 0;
      let errorMessages = [];
      let i = 0;
      const total = selectedStudentIds.size;

      for (const studentId of selectedStudentIds) {
        i++;
        const studentData = instituteStudents.find((s) => s._id === studentId);
        if (!studentData) continue;

        setCurrentStudentProcessing(
          `${studentData.firstName} ${studentData.lastName}`,
        );
        setBulkProgress(
          `Enrolling ${studentData.firstName} ${studentData.lastName} (${i}/${total})...`,
        );

        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/enrollment/add-student`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                classId: classId,
                firstName: studentData.firstName,
                lastName: studentData.lastName,
                referenceId: studentData.referenceId,
                email: studentData.email,
                phoneNumber: studentData.phoneNumber,
                receiveWhatsAppMessages:
                  studentData.receiveWhatsAppMessages || false,
                receiveEmailMessages: studentData.receiveEmailMessages || false,
              }),
            },
          );

          if (response && response.ok) {
            enrolledCount++;
          } else {
            const errorData = await response?.json();
            errorMessages.push(
              `${studentData.firstName} ${studentData.lastName}: ${
                errorData?.message || "Failed to enroll"
              }`,
            );
          }
        } catch (err) {
          errorMessages.push(
            `${studentData.firstName} ${studentData.lastName}: ${err.message}`,
          );
        }
        // Small delay to allow UI to update
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      setBulkPhase("completed");
      setBulkProgress(
        `Successfully enrolled ${enrolledCount} ${
          enrolledCount === 1 ? "student" : "students"
        }.`,
      );
      setCurrentStudentProcessing("");

      setTimeout(async () => {
        if (enrolledCount > 0) {
          swalSuccess(
            `${enrolledCount} ${
              enrolledCount === 1 ? "student" : "students"
            } enrolled successfully.`,
          );
        }

        if (errorMessages.length > 0) {
          swalError(
            `Some operations failed:\n${errorMessages.slice(0, 3).join("\n")}${
              errorMessages.length > 3
                ? `\n...and ${errorMessages.length - 3} more`
                : ""
            }`,
          );
        }

        await fetchClassStudents();
        closeInstituteEnrollModal();
        setIsEnrollingStudents(false);
        setBulkPhase("");
        setBulkProgress("");
      }, 1500);
    } catch (error) {
      swalError("Failed to enroll students");
      setIsEnrollingStudents(false);
      setBulkPhase("");
      setBulkProgress("");
    }
  };

  // Handle row selection for Enrolled Students table
  const handleEnrollmentRowSelect = (e, enrollmentId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedEnrollmentIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(enrollmentId)) {
        newSet.delete(enrollmentId);
      } else {
        newSet.add(enrollmentId);
      }
      return newSet;
    });
  };

  // Handle select all for Enrolled Students table
  const handleEnrollmentSelectAll = (visibleItemIds, areAllVisibleSelected) => {
    setSelectedEnrollmentIds((prev) => {
      const newSet = new Set(prev);
      if (areAllVisibleSelected) {
        // Deselect all visible
        visibleItemIds.forEach((id) => newSet.delete(id));
      } else {
        // Select all visible
        visibleItemIds.forEach((id) => newSet.add(id));
      }
      return newSet;
    });
  };

  // Handle Bulk Delete Enrollments
  const handleBulkDeleteEnrollments = async () => {
    if (selectedEnrollmentIds.size === 0) return;

    const confirmResult = await swalConfirm(
      "Unenroll selected students?",
      `Are you sure you want to unenroll ${selectedEnrollmentIds.size} student(s) from this class?`,
      "Yes, Unenroll!",
      "No, Cancel",
    );

    if (!confirmResult.isConfirmed) return;

    setIsBulkDeleting(true);

    const studentsToUnenroll = students.filter((s) =>
      selectedEnrollmentIds.has(s.enrollmentId),
    );
    const successRecords = [];
    const errorRecords = [];

    // We can use Promise.all to send requests in parallel
    // or sequential if we want to be safer. Parallel is fine here.
    const promises = studentsToUnenroll.map(async (student) => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/enrollment/delete/${student.enrollmentId}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );
        if (response && response.ok) {
          return { student, success: true };
        }
        const errorData = await response.json().catch(() => ({}));
        return {
          student,
          success: false,
          error: errorData.message || "Failed to remove",
        };
      } catch (error) {
        console.error(
          `Failed to delete enrollment ${student.enrollmentId}`,
          error,
        );
        return {
          student,
          success: false,
          error: error.message || "Network error",
        };
      }
    });

    try {
      const results = await Promise.all(promises);

      const successfulIds = new Set();
      results.forEach((res) => {
        if (res.success) {
          successfulIds.add(res.student.enrollmentId);
          successRecords.push(res.student);
        } else {
          errorRecords.push({ ...res.student, error: res.error });
        }
      });

      // Update local state by filtering out deleted enrollments
      setStudents((prev) =>
        prev.filter((s) => !successfulIds.has(s.enrollmentId)),
      );

      // Clear selection
      setSelectedEnrollmentIds(new Set());

      // Create summary HTML
      const successRowsHtml = successRecords
        .map(
          (s) =>
            `<tr style="background: #d4edda;">
          <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724;">✓</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.firstName} ${s.lastName}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724; font-weight: 600;">Removed</td>
        </tr>`,
        )
        .join("");

      const errorRowsHtml = errorRecords
        .map(
          (s) =>
            `<tr style="background: #f8d7da;">
          <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">✗</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.firstName} ${s.lastName}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">${s.error}</td>
        </tr>`,
        )
        .join("");

      const tableHtml = `
        <div style="max-height: 400px; overflow: auto; margin-top: 10px; -webkit-overflow-scrolling: touch;">
          <div style="min-width: 600px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; white-space: nowrap;">
              <thead>
                <tr style="background: #f8f9fa; position: sticky; top: 0;">
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center; width: 40px;"></th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center;">Student Name</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${successRowsHtml}
                ${errorRowsHtml}
              </tbody>
            </table>
          </div>
        </div>
        <p style="margin-top: 8px; font-size: 0.75em; color: #888; text-align: center;">Swipe horizontally to see all columns</p>
        <div style="margin-top: 12px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
          <span style="color: #155724; font-weight: 600;">✓ Removed: ${successRecords.length}</span>
          <span style="color: #721c24; font-weight: 600;">✗ Failed: ${errorRecords.length}</span>
        </div>
      `;

      const icon =
        errorRecords.length === 0
          ? "success"
          : successRecords.length === 0
            ? "error"
            : "info";
      const title =
        errorRecords.length === 0
          ? "All Selected Students Removed"
          : successRecords.length === 0
            ? "Removal Failed"
            : "Removal Summary";

      Swal.fire({
        icon: icon,
        title: title,
        html: tableHtml,
        width: "90%",
        confirmButtonText: "Got it!",
      });
    } catch (err) {
      console.error("Bulk delete error:", err);
      swalError("An error occurred during bulk removal.");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Columns for enrolled students table
  const columns = [
    { key: "name", label: "Student Name", sortable: true },
    { key: "referenceId", label: "ID", sortable: true },
    { key: "rollNo", label: "Roll No", sortable: true },
    hasLevelData && {
      key: "level",
      label: isLanguageInst ? "CEFR Level" : "Level",
      sortable: true,
      filter: true,
    },
    hasBatchData && {
      key: "batch",
      label: isLanguageInst ? "Batch" : "Batch",
      sortable: true,
      filter: true,
    },
    { key: "email", label: "Email", sortable: true },
    { key: "phoneNumber", label: "Phone Number", sortable: true },
    { key: "isWhatsApp", label: "WhatsApp", sortable: false },
    { key: "isEmail", label: "Email Message", sortable: false },
  ].filter(Boolean);

  // Columns for enroll modal table
  const enrollModalColumns = [
    { key: "name", label: "Student Name", sortable: true },
    { key: "referenceId", label: "Reference ID", sortable: true },
    { key: "rollNo", label: "Roll No", sortable: true },
    levelOptions.length > 0 &&
      instituteCategory !== "HIGHER_EDUCATION" && {
        key: "level",
        label: isLanguageInst ? "CEFR Level" : "Level",
        sortable: true,
      },
    batchOptions.length > 0 &&
      instituteCategory === "HIGHER_EDUCATION" && {
        key: "batch",
        label: "Batch",
        sortable: true,
      },
    { key: "email", label: "Email", sortable: true },
  ].filter(Boolean);

  const enhancedStudents = students.map((student) => {
    return {
      ...student,
      _id: student.enrollmentId, // Table uses _id for selection, mapping enrollmentId to it
      level:
        isLanguageInst && student.level ? (
          <span
            className={`cefr-badge cefr-${String(student.level)
              .toLowerCase()
              .replace(/\./g, "-")}`}
          >
            {student.level}
          </span>
        ) : (
          student.level
        ),
      isWhatsApp: student.receiveWhatsAppMessages ? "Yes" : "No",
      isEmail: student.receiveEmailMessages ? "Yes" : "No",
    };
  });

  // Enhanced data for enroll modal
  const enhancedAvailableStudents = availableStudents.map((stu) => ({
    ...stu,
    batch: stu.batch || "-",
    level:
      isLanguageInst && stu.level ? (
        <span
          className={`cefr-badge cefr-${String(stu.level)
            .toLowerCase()
            .replace(/\./g, "-")}`}
        >
          {stu.level}
        </span>
      ) : (
        stu.level || "-"
      ),
  }));

  return (
    <>
      {/* Institute Enroll Modal */}
      <Modal
        isOpen={isInstituteEnrollModalOpen}
        onClose={() => {
          if (!isEnrollingStudents) {
            closeInstituteEnrollModal();
          }
        }}
        maxWidth="95%"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "1rem",
          }}
        >
          <div>
            <h2 style={{ marginBottom: "0.5rem", color: "#1a1a2e" }}>
              Enroll Students
            </h2>
            <p style={{ color: "#666", fontSize: "0.9rem", margin: 0 }}>
              Select students to enroll in this class.
            </p>
          </div>
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
            className="enrollment-filters"
          >
            <label
              className={`excel-upload-btn ${
                isParsingExcel || isEnrollingStudents || isLoadingModalData
                  ? "disabled"
                  : ""
              }`}
              style={{
                cursor:
                  isParsingExcel || isEnrollingStudents || isLoadingModalData
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  isParsingExcel || isEnrollingStudents || isLoadingModalData
                    ? 0.6
                    : 1,
                margin: 0,
              }}
            >
              {isParsingExcel ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <FontAwesomeIcon icon={faFileExcel} />
              )}
              <span>
                {isParsingExcel
                  ? "Reading excel file..."
                  : "Autofill from Excel"}
              </span>
              <input
                ref={excelFileInputRef}
                type="file"
                accept=".xlsx, .xls, .csv"
                onChange={handleExcelUpload}
                style={{ display: "none" }}
                disabled={
                  isEnrollingStudents || isLoadingModalData || isParsingExcel
                }
              />
            </label>

            {/* Level filter for non-HIGHER_EDUCATION */}
            {instituteCategory !== "HIGHER_EDUCATION" &&
              levelOptions.length > 0 && (
                <div style={{ minWidth: "150px" }}>
                  <Select
                    value={selectedLevel}
                    onChange={(selected) => {
                      setSelectedLevel(selected);
                      setSelectedStudentIds(new Set()); // Reset selection on filter change
                    }}
                    options={levelOptions}
                    isDisabled={
                      isEnrollingStudents ||
                      isLoadingModalData ||
                      isParsingExcel
                    }
                    classNamePrefix="react-select"
                    className="batch-select"
                    menuPlacement="bottom"
                    placeholder="Level"
                  />
                </div>
              )}
            {/* Batch filter for HIGHER_EDUCATION */}
            {instituteCategory === "HIGHER_EDUCATION" &&
              batchOptions.length > 0 && (
                <div style={{ minWidth: "150px" }}>
                  <Select
                    value={selectedBatch}
                    onChange={(selected) => {
                      setSelectedBatch(selected);
                      setSelectedStudentIds(new Set()); // Reset selection on filter change
                    }}
                    options={batchOptions}
                    isDisabled={
                      isEnrollingStudents ||
                      isLoadingModalData ||
                      isParsingExcel
                    }
                    classNamePrefix="react-select"
                    className="batch-select"
                    menuPlacement="bottom"
                    placeholder="Batch"
                  />
                </div>
              )}
          </div>
        </div>

        {isEnrollingStudents ? (
          <div className="bulk-progress-container">
            <div
              className="progress-indicator"
              style={{
                background:
                  bulkPhase === "enrolling"
                    ? "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)"
                    : bulkPhase === "completed"
                      ? "linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)"
                      : "#f8f9fa",
                border: `2px solid ${
                  bulkPhase === "enrolling"
                    ? "#4caf50"
                    : bulkPhase === "completed"
                      ? "#2e7d32"
                      : "#e0e0e0"
                }`,
              }}
            >
              {bulkPhase !== "completed" ? (
                <Loader
                  fontSize="50px"
                  style={{
                    left: "0",
                    color: bulkPhase === "enrolling" ? "#388e3c" : "#666",
                  }}
                />
              ) : (
                <div className="success-icon">
                  <i className="fa-solid fa-check-circle"></i>
                </div>
              )}

              <div className="progress-text">
                <div className="progress-title">{bulkProgress}</div>

                {currentStudentProcessing && bulkPhase === "enrolling" && (
                  <div className="current-student">
                    <i className="fa-solid fa-user"></i>
                    {currentStudentProcessing}
                  </div>
                )}

                <div className="progress-description">
                  {bulkPhase === "enrolling" &&
                    "Enrolling students to class..."}
                  {bulkPhase === "completed" &&
                    "All students enrolled successfully!"}
                </div>
              </div>
            </div>
          </div>
        ) : isLoadingModalData ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <Loader style={{ fontSize: "2rem", left: "0" }} />
          </div>
        ) : availableStudents.length === 0 ? (
          <div
            className="empty-state"
            style={{ textAlign: "center", padding: "2rem" }}
          >
            <i
              className="fa-solid fa-users-slash"
              style={{
                fontSize: "2rem",
                marginBottom: "1rem",
                display: "block",
                color: "#94a3b8",
              }}
            ></i>
            <p style={{ color: "#666" }}>
              {instituteCategory !== "HIGHER_EDUCATION" && !selectedLevel
                ? "Please select a level to view students."
                : instituteCategory === "HIGHER_EDUCATION" && !selectedBatch
                  ? "Please select a batch to view students."
                  : instituteStudents.length === 0
                    ? "No students found in your institute."
                    : "All students from this selection are already enrolled in this class."}
            </p>
          </div>
        ) : (
          <>
            <div style={{ maxHeight: "400px", overflow: "auto" }}>
              <Table
                caption="Students Not Enrolled"
                ref={tableRef}
                data={enhancedAvailableStudents}
                columns={enrollModalColumns}
                isCheckBoxAvailable={true}
                filter={false}
                selectedRowIds={selectedStudentIds}
                onRowSelect={handleRowSelect}
                onSelectAll={handleSelectAll}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
                gap: "1rem",
              }}
            >
              <button
                className="create-btn"
                onClick={handleBulkEnrollment}
                disabled={selectedStudentIds.size === 0}
              >
                Enroll Selected ({selectedStudentIds.size})
              </button>
            </div>
          </>
        )}
      </Modal>

      <div className="manage-container">
        <div
          className="header"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          {students.length > 0 && (
            <button
              onClick={exportToExcel}
              className="btn-export"
              disabled={students.length === 0}
              title="Export enrollments to Excel"
            >
              <FontAwesomeIcon icon={faDownload} /> Export to Excel
            </button>
          )}
          <button
            onClick={handleBulkDeleteEnrollments}
            className="btn-export" // Reusing btn-export for base styles if possible, but overriding color
            style={{
              backgroundColor: "#ef4444",
              color: "white",
              border: "none",
              marginRight: "0.5rem",
            }}
            disabled={isBulkDeleting || selectedEnrollmentIds.size === 0}
            title="Unenroll selected students"
          >
            {isBulkDeleting ? (
              <i className="fa-solid fa-spinner fa-spin"></i>
            ) : (
              <i className="fa-solid fa-trash"></i>
            )}
            &nbsp; Unenroll Selected ({selectedEnrollmentIds.size})
          </button>
          <button
            onClick={() => handleDownloadAllQR()}
            className="btn-upload"
            disabled={downloadingAllQR || students.length === 0}
          >
            {downloadingAllQR ? (
              <Loader
                style={{ fontSize: "1.3rem", left: "0", color: "white" }}
              />
            ) : (
              <>
                <i className="fa-solid fa-qrcode"></i> Download All QR Codes
              </>
            )}
          </button>
          <button
            className="create-btn"
            onClick={() => openInstituteEnrollModal()}
          >
            <FontAwesomeIcon
              icon={faUserPlus}
              style={{ marginRight: "0.5rem" }}
            />
            {isLanguageInst ? "Enroll in Batch" : "Enroll Students"}
          </button>
        </div>
        {loading && <Loader style={{ fontSize: "50px" }} />}

        {error && <div className="error-message">{error}</div>}
        <Table
          data={enhancedStudents}
          columns={columns}
          caption={isLanguageInst ? "Batch Students" : "Enrolled Students"}
          onDataFiltered={handleDataFiltered}
          isCheckBoxAvailable={true}
          selectedRowIds={selectedEnrollmentIds}
          onRowSelect={handleEnrollmentRowSelect}
          onSelectAll={handleEnrollmentSelectAll}
          disableRowClickSelection={true}
        />
      </div>
    </>
  );
});

ManageEnrollments.propTypes = {
  classId: PropTypes.string.isRequired,
};

export default ManageEnrollments;
